/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1690156800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Jul 24 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "workday",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Workday",
          "depth": 0,
          "id": "connect-to-workday"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing organizations",
          "depth": 1,
          "id": "syncing-organizations"
        }, {
          "value": "Syncing workers",
          "depth": 1,
          "id": "syncing-workers"
        }, {
          "value": "Syncing positions",
          "depth": 1,
          "id": "syncing-positions"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      ul: "ul",
      li: "li",
      code: "code",
      h3: "h3",
      h4: "h4",
      em: "em"
    }, _provideComponents(), props.components), {CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Organizations"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Workday organizations"
            }), _jsx(_components.td, {
              children: "Upsert, Update"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://community.workday.com/sites/default/files/file-hosting/productionapi/Human_Resources/v40.1/Add_Update_Organization.html",
                children: "Organization docs"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Workers"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Workday workers"
            }), _jsx(_components.td, {
              children: "Update"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.a, {
                href: "https://community.workday.com/sites/default/files/file-hosting/productionapi/Human_Resources/v40.1/Change_Home_Contact_Information.html",
                children: "Home contact information"
              }), ", ", _jsx(_components.a, {
                href: "https://community.workday.com/sites/default/files/file-hosting/productionapi/Human_Resources/v40.1/Change_Work_Contact_Information.html",
                children: "work contact information"
              }), ", and ", _jsx(_components.a, {
                href: "https://community.workday.com/sites/default/files/file-hosting/productionapi/Human_Resources/v40.1/Change_Personal_Information.html",
                children: "personal information docs"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Positions"
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Workday positions"
            }), _jsx(_components.td, {
              children: "Update"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://community.workday.com/sites/default/files/file-hosting/productionapi/Staffing/v40.1/Edit_Position.html",
                children: "Position docs"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-workday",
        children: "Connect to Workday"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Workday"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Workday"
        }), " by entering the following required fields into Hightouch:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Hostname"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Tenant ID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Username"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Password"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "hostname"
        }), " is the hostname of the Workday SOAP endpoints you want to use.\nYou only need to enter the hostname, without ", _jsx(_components.code, {
          children: "https://"
        }), " nor particular endpoint URL."]
      }), "\n", _jsxs(_components.p, {
        children: ["For example, if you want to use this SOAP endpoint: ", _jsx(_components.code, {
          children: "https://wd2-impl-services1.workday.com/ccx/service/your_company/Human_Resources/v40.1"
        }), ",\nenter ", _jsx(_components.code, {
          children: "wd2-impl-services1.workday.com"
        }), ".\nIf you don't have a hostname, enter ", _jsx(_components.code, {
          children: "wd2-impl-services1.workday.com"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can find your ", _jsx(_components.strong, {
          children: "tenant ID"
        }), " from your URL when you are logged into Workday.\nFor example, if the URL is ", _jsx(_components.code, {
          children: "https://impl.workday.com/your_company/d/home.html"
        }), ", then your tenant ID is ", _jsx(_components.code, {
          children: "your_company"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Workday recommends that you use Integrated System User (ISU) to when connecting to Workday via third-party services like Hightouch.\nSee ", _jsx(_components.a, {
          href: "https://doc.workday.com/reader/Z9lz_01hqDMDg6NSf7wCBQ/esBDCh5D66sgBhIxmQ5U5g",
          children: "Workday's docs"
        }), " for more information on setting up an ISU."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Workday destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Workday destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-organizations",
        children: "Syncing organizations"
      }), "\n", _jsx(_components.p, {
        children: "This sync type adds new organizations and/or updates existing organizations with the supplied information.\nA new effective-dated organization name is automatically created if any attributes used for organization name are different as of specified effective date."
      }), "\n", _jsxs(_components.p, {
        children: ["To use this sync type, you need to have ", _jsx(_components.strong, {
          children: "Put"
        }), " access for the ", _jsx(_components.strong, {
          children: "Manage: Organization Update Integration"
        }), " domain."]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "To match rows from your model to organization in Workday, you need to select a model column and corresponding Workday field."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["In ", _jsx(_components.strong, {
            children: "upsert"
          }), " mode, you can match on either ", _jsx(_components.strong, {
            children: "Organization Reference ID"
          }), " or ", _jsx(_components.strong, {
            children: "Integration ID Data"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["In ", _jsx(_components.strong, {
            children: "update"
          }), " mode, you must match on ", _jsx(_components.strong, {
            children: "Integration ID Data"
          }), "."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If you are matching with ", _jsx(_components.strong, {
          children: "Integration ID Data"
        }), ", you must also provide the ", _jsx(_components.strong, {
          children: "System ID"
        }), " in the attributes mapping."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you sync organization fields via ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), ".\nYou can map data from any of your model columns to the default organization fields.\nEnsure your model columns data types match the data types of the fields you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-workers",
        children: "Syncing workers"
      }), "\n", _jsx(_components.p, {
        children: "This sync type updates existing workers in Workday."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "To match rows from your model to worker in Workday, you need to select a model column and corresponding Workday field. You can match on any of the following Workday fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Universal_ID_Reference"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Person_Reference"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["When matching with ", _jsx(_components.strong, {
          children: "Universal_ID_Reference"
        }), ", you must also specify its ", _jsx(_components.strong, {
          children: "Reference Type"
        }), " int the attributes mapping. Valid values are:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "WID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Universal_Identifier_ID"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["When matching on ", _jsx(_components.strong, {
          children: "Person_Reference"
        }), ", you must also specify its ", _jsx(_components.strong, {
          children: "Reference Type"
        }), " in the attributes mapping.\nSee the ", _jsx(_components.a, {
          href: "https://community.workday.com/sites/default/files/file-hosting/productionapi/Human_Resources/v40.1/Change_Home_Contact_Information.html#RoleObjectIDType",
          children: "RoleObject"
        }), "'s ", _jsx(_components.code, {
          children: "@type"
        }), " for the full list of valid values."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-1",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you use ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), " to update the following kinds of worker information:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://community.workday.com/sites/default/files/file-hosting/productionapi/Human_Resources/v40.1/Change_Home_Contact_Information.html#Person_Contact_Information_DataType",
            children: "home contact information"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://community.workday.com/sites/default/files/file-hosting/productionapi/Human_Resources/v40.1/Change_Work_Contact_Information.html#Person_Contact_Information_DataType",
            children: "work contact information"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://community.workday.com/sites/default/files/file-hosting/productionapi/Human_Resources/v40.1/Change_Personal_Information.html#Change_Personal_Information_DataType",
            children: "personal information"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Field names for ", _jsx(_components.a, {
          href: "https://community.workday.com/sites/default/files/file-hosting/productionapi/Human_Resources/v40.1/Change_Home_Contact_Information.html#Person_Contact_Information_DataType",
          children: "home contact information"
        }), " begin with ", _jsx(_components.em, {
          children: "Home"
        }), ",\nfield names for ", _jsx(_components.a, {
          href: "https://community.workday.com/sites/default/files/file-hosting/productionapi/Human_Resources/v40.1/Change_Work_Contact_Information.html#Person_Contact_Information_DataType",
          children: "work contact information"
        }), " begin with ", _jsx(_components.em, {
          children: "Work"
        }), ", and field names for ", _jsx(_components.a, {
          href: "https://community.workday.com/sites/default/files/file-hosting/productionapi/Human_Resources/v40.1/Change_Personal_Information.html#Change_Personal_Information_DataType",
          children: "personal information"
        }), " begin with ", _jsx(_components.em, {
          children: "Personal Information"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "You can map data from any of your model columns to these fields. Ensure your model columns data types match the data types of the fields you want to sync to."
      }), "\n", _jsx(_components.h3, {
        id: "syncing-positions",
        children: "Syncing positions"
      }), "\n", _jsx(_components.p, {
        children: "This sync type updates existing positions in Workday."
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-2",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "To match rows from your model to position in Workday, you need to select a model column and corresponding Workday field. You can match on any of the following Workday fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "WID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Position_ID"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping-2",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you sync position fields via ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), ".\nYou can map data from any of your model columns to the default position fields.\nEnsure your model columns data types match the data types of the fields you want to sync to."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "the-task-submitted-is-not-authorized",
        children: "The task submitted is not authorized"
      }), "\n", _jsxs(_components.p, {
        children: ["This error means your Workday user doesn't have enough permission to perform the API operation configured in the Hightouch sync.\nYou can resolve the error by granting the user you ", _jsx(_components.a, {
          href: "#connect-to-workday",
          children: "authenticated with"
        }), " with the permission required for the endpoint."]
      }), "\n", _jsxs(_components.p, {
        children: ["For example, if you get this error when you're trying to make changes with the ", _jsx(_components.a, {
          href: "https://community.workday.com/sites/default/files/file-hosting/productionapi/Human_Resources/v40.1/Change_Home_Contact_Information.html",
          children: "Change_Home_Contact_Information"
        }), " endpoint, you should ensure your user has permission to use this API."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
